/*

  SensorVariable Class - Store data resulting from a request for a particular sensor variable.

  This Class is designed to hold data from a call to the API for data at several stations for a specific
  sensor variable.  This Class hold ONLY data for a specific variable, and the methods only return data
  directly to that variable and no other variables available at a particular station.

  Upon initialization, this Class analyzes which stations passed in actually have data for the requested
  variable, and then makes that data available by filtering and mapping.

  Utility methods make it easy to use the "key" for a variable to retrieved the data via the actual sensor
  name.

 */

import { startCase, find } from 'lodash';
import { DateTime } from 'luxon';
import { sampleStandardDeviation, mean } from 'simple-statistics/index';

const variablesToAnomalize = [
  'snow_depth',
  'air_temp',
  'precip_accum',
  'wind_speed',
];

export default class SensorVariable {

  constructor({ data, range, variable, options }) {
    if (!range) {
      const error = 'A SensorVariable Class cannot be constructed without a date range for the sensor variable.  ' +
        'Make sure the "range" property has been passed as an argument.';
      throw new Error(error);
    }

    if (!variable) {
      const error = 'A SensorVariable Class cannot be constructed without a name for the sensor variable.  ' +
        'Make sure the "sensorVariable" property has been passed as an argument.';
      throw new Error(error);
    }

    this.name = variable;
    this.key = Object.keys(data.timeseries.STATION[0].SENSOR_VARIABLES[variable])[0];
    this.units = data.timeseries.UNITS[variable];
    this.label = startCase(this.name.replace(/_/g, ' '));
    this.fulllabel = `${startCase(this.name.replace(/_/g, ' '))} (${this.units})`;
    this.range = range;

    const stations = data.stats.STATION;
    let totalStandardDeviation;
    let totalAverageMedian;

    if (stations.length > 1) {
      totalStandardDeviation = sampleStandardDeviation(stations.map(st => (st.STATISTICS[this.key] ? st.STATISTICS[this.key].median : null)));
      totalAverageMedian = mean(stations.map(st => (st.STATISTICS[this.key] ? st.STATISTICS[this.key].median : null)));
    }

    const mapData = (station) => {
      const dateTimeObservations = station.OBSERVATIONS.date_time;
      const matchingStat = find(data.stats.STATION, { STID: station.STID });
      const stationStats = matchingStat.STATISTICS[this.key];

      const observations = [];
      const tableData = [];

      dateTimeObservations.forEach((dateTime, index) => {
        const obsMap = {
          x: DateTime.fromISO(dateTime).toJSDate(),
          y: station.OBSERVATIONS[this.key][index],
          // y: Math.round(station.OBSERVATIONS[this.key][index]),
          id: station.STID,
        };

        let valueIsAnomaly = false;
        const diff = Math.abs(obsMap.y - stationStats.median);
        const minMaxDiff = stationStats.maximum - stationStats.minimum;
        let maxAllowedDifference = stationStats.standard_deviation * 4;

        if (minMaxDiff >= 100) {
          maxAllowedDifference = stationStats.standard_deviation * 1;
        }

        if (options.removeAnomalies && variablesToAnomalize.includes(variable)) {
          valueIsAnomaly = (diff >= (maxAllowedDifference) && minMaxDiff !== 0)
            || Math.abs(stationStats.median - totalAverageMedian) > totalStandardDeviation * 2;
        }

        // if (station.STID === 'JEMC1') {
          // console.log('-');
          // console.log('totalStandardDeviation * 4', totalStandardDeviation * 2);
          // console.log('totalStandardDeviation', totalStandardDeviation);
          // console.log('totalAverageMedian', totalAverageMedian);
          // console.log('stationStats.median', stationStats.median);
          // console.log('Math.abs(stationStats.median - totalAverageMedian)', Math.abs(stationStats.median - totalAverageMedian));
          // console.log(stationStats);
          // console.log(obsMap.y, minMaxDiff);
          // console.log(diff);
          // console.log(minMaxDiff);
          // console.log(maxAllowedDifference);
        // }

        // const badValue = obsMap.y === null || valueIsAnomaly;

        if (!valueIsAnomaly) {
          observations.push(obsMap);
        }
        // observations.push(obsMap);

        tableData.push({
          dateTime: obsMap.x,
          value: obsMap.y,
          id: station.STID,
          anomaly: valueIsAnomaly,
        });
      });

      return {
        stationId: station.STID,
        observations,
        tableData,
        statistics: stationStats,
      };
    };

    this.stations = data.timeseries.STATION.map(mapData);
  }

}



// WEBPACK FOOTER //
// ./src/models/SensorVariable.js